import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAppStore from '../store/useAppStore';
import { UserRole, Stage } from '../types';
import styles from './Onboarding.module.css';

const roles: { key: UserRole; label: string; icon: string }[] = [
  { key: '爸爸', label: '我是爸爸', icon: '👨' },
  { key: '妈妈', label: '我是妈妈', icon: '👩' },
  { key: '祖辈', label: '我是祖辈', icon: '👴' },
  { key: '其他照护者', label: '其他照护者', icon: '🤱' },
];

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const setOnboarding = useAppStore((s) => s.setOnboarding);

  const [step, setStep] = useState<'role' | 'baby' | 'stage'>('role');
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [babyBorn, setBabyBorn] = useState<boolean | null>(null);
  const [dateValue, setDateValue] = useState('');

  const handleRoleSelect = (role: UserRole) => {
    setSelectedRole(role);
  };

  const handleNextToBaby = () => {
    if (!selectedRole) return;
    setStep('baby');
  };

  const handleBornSelect = (born: boolean) => {
    setBabyBorn(born);
    setDateValue('');
  };

  const handleNextToStage = () => {
    if (babyBorn === null || !dateValue) return;

    const date = new Date(dateValue);
    let stage = Stage.NEWBORN;

    if (babyBorn) {
      const now = new Date();
      const monthsDiff = (now.getFullYear() - date.getFullYear()) * 12 + now.getMonth() - date.getMonth();
      const daysDiff = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

      if (daysDiff <= 28) stage = Stage.NEWBORN;
      else if (monthsDiff <= 3) stage = Stage.ONE_TO_THREE;
      else if (monthsDiff <= 6) stage = Stage.FOUR_TO_SIX;
      else if (monthsDiff <= 12) stage = Stage.SEVEN_TO_TWELVE;
      else stage = Stage.NEWBORN; // 超出范围默认新生儿期演示
    } else {
      // 预产期：统一从新生儿期开始
      stage = Stage.NEWBORN;
    }

    const dueDate = babyBorn ? '' : dateValue;
    const birthDate = babyBorn ? dateValue : '';
    setOnboarding(selectedRole!, babyBorn!, birthDate, dueDate, stage);
    setStep('stage');
  };

  const handleStart = () => {
    navigate('/map');
  };

  return (
    <div className={styles.container}>
      {step === 'role' && (
        <>
          <div className={styles.header}>
            <button className={styles.homeLink} onClick={() => navigate('/')}>
              ← 返回首页
            </button>
            <h1 className={styles.title}>欢迎来到育儿成长闯关</h1>
            <p className={styles.subtitle}>先告诉我们你的身份，为你推荐合适的学习路径</p>
          </div>

          <div className={styles.card}>
            <h2 className={styles.stepTitle}>🎯 选择你的身份</h2>
            <div className={styles.roleGrid}>
              {roles.map((r) => (
                <button
                  key={r.key}
                  className={`${styles.roleBtn} ${selectedRole === r.key ? styles.roleActive : ''}`}
                  onClick={() => handleRoleSelect(r.key)}
                >
                  <span className={styles.roleIcon}>{r.icon}</span>
                  <span className={styles.roleLabel}>{r.label}</span>
                </button>
              ))}
            </div>
            <button
              className={styles.nextBtn}
              disabled={!selectedRole}
              onClick={handleNextToBaby}
            >
              下一步
            </button>
          </div>
        </>
      )}

      {step === 'baby' && (
        <>
          <div className={styles.header}>
            <h1 className={styles.title}>关于宝宝</h1>
            <p className={styles.subtitle}>让系统为你的宝宝推荐最适合的学习阶段</p>
          </div>

          <div className={styles.card}>
            <h2 className={styles.stepTitle}>👶 宝宝状态</h2>
            <div className={styles.bornToggle}>
              <button
                className={`${styles.toggleBtn} ${babyBorn === true ? styles.toggleActive : ''}`}
                onClick={() => handleBornSelect(true)}
              >
                已经出生
              </button>
              <button
                className={`${styles.toggleBtn} ${babyBorn === false ? styles.toggleActive : ''}`}
                onClick={() => handleBornSelect(false)}
              >
                还没出生
              </button>
            </div>

            {babyBorn !== null && (
              <div className={styles.dateSection}>
                <label className={styles.dateLabel}>
                  {babyBorn ? '宝宝出生日期' : '预产期'}
                </label>
                <input
                  type="date"
                  className={styles.dateInput}
                  value={dateValue}
                  onChange={(e) => setDateValue(e.target.value)}
                  {...(babyBorn
                    ? { max: new Date().toISOString().split('T')[0] }
                    : {
                        min: new Date().toISOString().split('T')[0],
                        max: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                      }
                  )}
                />
              </div>
            )}

            <div className={styles.btnRow}>
              <button className={styles.backBtn} onClick={() => { setStep('role'); setBabyBorn(null); }}>
                ← 返回
              </button>
              <button
                className={styles.nextBtn}
                disabled={babyBorn === null || !dateValue}
                onClick={handleNextToStage}
              >
                确认并继续
              </button>
            </div>
          </div>
        </>
      )}

      {step === 'stage' && (
        <>
          <div className={styles.header}>
            <h1 className={styles.title}>已为你匹配学习阶段</h1>
          </div>

          <div className={styles.resultCard}>
            <div className={styles.resultIcon}>🎉</div>
            <h2 className={styles.resultStage}>
              当前阶段：{useAppStore.getState().currentStage}
            </h2>
            <p className={styles.resultDesc}>
              {babyBorn
                ? `宝宝出生于 ${dateValue}，系统已自动匹配最适合的学习阶段。`
                : `预计 ${dateValue} 迎来宝宝，从现在开始学习，做好充分准备。`}
            </p>
            <div className={styles.resultTips}>
              <div className={styles.tipItem}>
                <span>📖</span> 按阶段系统学习育儿知识
              </div>
              <div className={styles.tipItem}>
                <span>📝</span> 情景练习巩固实用技能
              </div>
              <div className={styles.tipItem}>
                <span>📊</span> 量化掌握情况，知道哪里薄弱
              </div>
            </div>
            <button className={styles.startBtn} onClick={handleStart}>
              进入阶段地图，开始学习
            </button>
          </div>
        </>
      )}

      {/* 步骤指示器 */}
      <div className={styles.steps}>
        <span className={`${styles.stepDot} ${styles.stepDone}`}>1</span>
        <span className={styles.stepLine} />
        <span className={`${styles.stepDot} ${step !== 'role' ? styles.stepDone : ''}`}>2</span>
        <span className={styles.stepLine} />
        <span className={`${styles.stepDot} ${step === 'stage' ? styles.stepDone : ''}`}>3</span>
      </div>
    </div>
  );
};

export default Onboarding;